import { useCallback, useEffect, useRef, useState } from 'react';
import ApiService, { SummaryGenerationOptions } from '../services/apiService';
import { SummaryUpdateError, SummaryUpdateInterruptedError, SummaryRequestNotStartedError } from '../services/summaryUpdateStream';
import { Recording, Summary } from '../types';
import { readGenerationAttempt, SummaryGenerationAttempt, writeGenerationAttempt } from '../utils/summaryGenerationSession';
import { SummaryPreviewScheduler, withSummaryUpdateLock } from '../utils/summaryUpdateSession';

interface GenerationState {
    blocked: boolean;
    running: boolean;
    streaming: boolean;
    hasResult: boolean;
    refreshing: boolean;
    phase: string;
    message: string;
    error: string;
    canRetry: boolean;
}

interface Options {
    api: ApiService;
    scope: string;
    fileId: string;
    meetingType: string;
    templateUuid?: string | null;
    t: (key: string) => string;
    onBlock: () => void;
    onStart: () => void;
    onPreview: (source: string) => void;
    onResult: (source: string) => void;
    onFailure: () => void;
    onLoaded: (summary: Summary, summaries: Summary[] | null, recording: Recording | null) => Promise<void>;
}

/** Generation streams into a merge session; provisional content is never uploaded. */
export function useSummaryGeneration(options: Options) {
    const callbacks = useRef(options);
    callbacks.current = options;
    const { api, scope, fileId, meetingType, templateUuid } = options;
    const summaryRef = { meetingType, templateUuid };
    const [state, setState] = useState<GenerationState>(() => ({
        blocked: !!readGenerationAttempt(scope), running: false, streaming: false, hasResult: false, refreshing: false, phase: 'preparing', message: '', error: '', canRetry: false,
    }));
    const blockedRef = useRef(state.blocked);
    const isBlocked = useCallback(() => blockedRef.current, []);
    const current = useRef<string | null>(null);
    const mounted = useRef(true);
    const previews = useRef<SummaryPreviewScheduler>();
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const t = (key: string) => callbacks.current.t(`summaryEditor.generation.${key}`);
    const patch = (next: Partial<GenerationState>) => setState(previous => ({ ...previous, ...next }));
    const isCurrent = (id: string) => mounted.current && current.current === id;
    const persist = (attempt: SummaryGenerationAttempt) => {
        // A detached stream must not overwrite a newer explicit attempt.
        if (readGenerationAttempt(scope)?.id === attempt.id) writeGenerationAttempt(scope, attempt);
    };
    const block = () => {
        blockedRef.current = true;
        callbacks.current.onBlock();
        patch({ blocked: true });
    };

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            current.current = null;
            clearTimeout(timer.current);
            previews.current?.finish();
            // Keep draining the stream under the document lock. Disconnecting
            // locally would not cancel the server task.
        };
    }, []);

    const reload = async (id: string, attempt: SummaryGenerationAttempt) => {
        const results = await Promise.allSettled([
            api.getSummary(fileId, summaryRef), api.getSummaries(fileId), api.getFile(fileId),
        ]);
        if (!isCurrent(id)) return false;
        const loaded = results[0].status === 'fulfilled' ? results[0].value : null;
        const summaries = results[1].status === 'fulfilled' ? results[1].value : null;
        // Het verslag staat nu in de lijst met zijn eigen template_uuid; ga daarmee verder
        const listed = summaries?.find(item => item.meetingType === meetingType);
        const summary = loaded && listed ? { ...loaded, templateUuid: listed.templateUuid } : loaded;
        const recording = results[2].status === 'fulfilled' ? results[2].value : null;
        if (!summary || !(summary.sourceContent ?? summary.content).trim()) {
            patch({ refreshing: false, message: attempt.saved ? t('savedRefreshFailed') : t('unknown') });
            return false;
        }
        await callbacks.current.onLoaded(summary, summaries, recording);
        if (!isCurrent(id)) return false;
        if (readGenerationAttempt(scope)?.id === attempt.id) writeGenerationAttempt(scope, null);
        blockedRef.current = false;
        patch({ blocked: false, hasResult: true, refreshing: false, canRetry: false,
            message: summaries && recording ? t('saved') : t('listsRefreshFailed'),
            ...(!summaries || !recording ? { error: t('listsRefreshFailed') } : {}) });
        return true;
    };

    const recover = async (attempt: SummaryGenerationAttempt, round = 0): Promise<void> => {
        if (current.current) return;
        clearTimeout(timer.current);
        const id = crypto.randomUUID();
        current.current = id;
        block();
        patch({ running: true, canRetry: false, phase: 'refreshing' });
        try {
            if (attempt.saved) {
                await reload(id, attempt);
                return;
            }
            const status = await api.getSummaryStatus(fileId, summaryRef);
            if (!isCurrent(id)) return;
            if (status === 'completed') await reload(id, attempt);
            else if (status === 'failed') patch({ message: t('failed'), canRetry: true });
            else {
                patch({ message: t(status === 'in_progress' ? 'inProgress' : 'unknown') });
                // Bounded, read-only recovery; even a 404 must never resend.
                if (status === 'in_progress' && round < 5) {
                    timer.current = setTimeout(() => { void recover(attempt, round + 1); }, Math.min(1000 * 2 ** round, 16000));
                }
            }
        } catch {
            if (isCurrent(id)) patch({ message: attempt.saved ? t('savedRefreshFailed') : t('unknown') });
        } finally {
            if (isCurrent(id)) { current.current = null; patch({ running: false }); }
        }
    };

    const start = async (additionalRecordings: string[], choices: SummaryGenerationOptions, explicitRetry = false) => {
        if (current.current) return;
        const previous = readGenerationAttempt(scope);
        if (previous && !explicitRetry) { await recover(previous); return; }
        const attempt: SummaryGenerationAttempt = { id: crypto.randomUUID(), fileId, meetingType, action: 'generate', saved: false };
        current.current = attempt.id;
        const isActive = () => isCurrent(attempt.id);
        block();
        patch({ running: true, streaming: false, hasResult: false, refreshing: false, phase: 'preparing', message: '', error: '', canRetry: false });
        let sent = false;
        const scheduler = new SummaryPreviewScheduler(preview => { if (isActive()) callbacks.current.onPreview(preview); });
        previews.current = scheduler;
        try {
            await withSummaryUpdateLock(scope, async () => {
                if (!isActive()) return;
                // Recheck after acquiring the cross-tab lock.
                const existing = readGenerationAttempt(scope);
                if (existing && (!explicitRetry || existing.id !== previous?.id)) {
                    patch({ message: t('inProgress') });
                    return;
                }
                callbacks.current.onStart();
                patch({ streaming: true });
                writeGenerationAttempt(scope, attempt);
                sent = true;
                for await (const update of api.streamSummary(fileId, summaryRef, additionalRecordings, choices)) {
                    if (typeof update.metadata?.task_id === 'string') { attempt.taskId = update.metadata.task_id; persist(attempt); }
                    if (update.event === 'result' && update.isComplete && update.result?.mode === 'generate' && update.result.saved) {
                        attempt.saved = true;
                        persist(attempt);
                        scheduler.finish();
                        if (isActive()) {
                            // The result is already saved. Show it immediately, even
                            // when the separate metadata refresh is slow or fails.
                            callbacks.current.onResult(update.result.summary);
                            patch({ running: false, streaming: false, hasResult: true, refreshing: true, phase: 'refreshing', message: t('refreshing') });
                            await reload(attempt.id, attempt);
                        }
                        return;
                    }
                    if (!isActive()) continue;
                    if (update.event === 'status' && update.phase) patch({ phase: update.phase });
                    if (update.event === 'preview' && update.content !== undefined) scheduler.preview(update.content);
                }
                throw new SummaryUpdateInterruptedError();
            });
        } catch (failure) {
            scheduler.finish();
            if (failure instanceof SummaryRequestNotStartedError) {
                sent = false;
                if (readGenerationAttempt(scope)?.id === attempt.id) writeGenerationAttempt(scope, null);
            }
            if (failure instanceof SummaryUpdateError && typeof failure.metadata?.task_id === 'string') {
                attempt.taskId = failure.metadata.task_id; persist(attempt);
            }
            if (isActive()) {
                if (attempt.saved) patch({ refreshing: false, message: t('savedRefreshFailed') });
                else {
                    callbacks.current.onFailure();
                    patch({ streaming: false, error: failure instanceof Error ? failure.message : t('failed'), canRetry: !sent });
                }
            }
        } finally {
            scheduler.finish();
            if (isActive()) {
                current.current = null;
                patch({ running: false });
                if (sent && !attempt.saved) await recover(attempt);
            }
        }
    };

    return { ...state, start, hasAttempt: () => !!readGenerationAttempt(scope), isBlocked,
        refresh: () => { const attempt = readGenerationAttempt(scope); if (attempt) void recover(attempt); } };
}
